"use client";

import Image from "next/image";
import Link from "next/link";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "../../lib/site";

/**
 * HubFooter — the marketing footer for the Dux AI hub.
 *
 * Layout: a brand block on the left (logo, mission line, Made-in mark)
 * and two balanced nav columns on the right — Company and Support.
 * The Support column carries the real contact details (phone, hours,
 * email) so the right half is filled by substance rather than a single
 * lonely link list. A hairline base bar closes it out with the legal
 * indicia on the left and a quiet wordmark on the right.
 *
 * Cookie preferences dispatches the existing dux:consent:open event so
 * the global CookieConsent modal opens from the footer.
 */
export default function HubFooter() {
  const openCookieModal = () => {
    document.dispatchEvent(new CustomEvent("dux:consent:open"));
  };

  return (
    <footer className="foot">
      <div className="foot-inner">
        {/* Brand */}
        <div className="foot-brand">
          <Image
            src="/dux-logo-glass.webp"
            alt="Dux AI"
            /* Glass-oval logo, natural ratio 800x394 (~2.03:1). The
               .foot-logo rule sets height; width:auto keeps ratio.
               sizes hint stops Next picking 828w for a ~60px box. */
            width={800}
            height={394}
            sizes="64px"
            className="foot-logo"
          />
          <p className="foot-desc">
            AI product trainers for the Dux and Thermann hot-water ranges —
            troubleshooting, install steps and spec look-ups, answered in
            seconds.
          </p>
          <span className="foot-madein">
            <span className="foot-madein-dot" aria-hidden="true" />
            Made in Australia · Moss Vale, NSW
          </span>
        </div>

        {/* Nav */}
        <nav className="foot-nav" aria-label="Footer">
          <div className="foot-col">
            <span className="foot-head">Company</span>
            <a
              className="foot-link"
              href="https://www.dux.com.au/about/"
              target="_blank"
              rel="noopener noreferrer"
            >
              About Dux
            </a>
            <Link className="foot-link" href="/privacy">
              Privacy
            </Link>
            <button
              type="button"
              className="foot-link foot-link-btn"
              onClick={openCookieModal}
            >
              Cookie preferences
            </button>
          </div>

          <div className="foot-col">
            <span className="foot-head">Support</span>
            <a className="foot-link" href={PHONE_HREF}>
              {PHONE}
            </a>
            <span className="foot-muted">Mon–Fri · 8 am – 5 pm AEST</span>
            <a className="foot-link" href={EMAIL_HREF}>
              {EMAIL}
            </a>
          </div>
        </nav>
      </div>

      {/* Base bar */}
      <div className="foot-base">
        <span className="foot-base-legal">
          © {new Date().getFullYear()} Dux Manufacturing Ltd.
          <span className="foot-base-sep" aria-hidden="true">·</span>
          Moss Vale, NSW Australia
        </span>
        <span className="foot-base-mark">Dux AI</span>
      </div>
    </footer>
  );
}
